// Portfolio Administration v1.0 PR 3 §7.2 — one row in the Setup tab list.

import { ChevronRight } from "lucide-react";
import { Badge } from "@/design-system/components/ui";
import {
  loanSetupTierLabel,
  type LoanSetupChecklistItemDto,
} from "@/api/portfolioAdmin/types";
import { deferredDueDateMessage, isDeferredDueDate } from "./setupCatalogForm";
import { daysLabel } from "../common/colorBand";
import { calendarDaysUntil } from "@/utils/datetime";

interface SetupChecklistRowProps {
  row: LoanSetupChecklistItemDto;
  catalogCode: string;
  catalogTitle: string;
  catalogTier: string;
  catalogOwnerRole: string;
  catalogDueDateFormula: string | null;
  onOpen: () => void;
}

const TIER_CLASS: Record<string, string> = {
  DealKiller: "border-l-status-error",
  RedFlag: "border-l-status-warning",
  NeedsImprovement: "border-l-status-info",
};

// Rows now group by workflow section (not tier), so each row carries a
// small tier chip using the same status tokens as the left border.
const TIER_TEXT_CLASS: Record<string, string> = {
  DealKiller: "text-status-error",
  RedFlag: "text-status-warning",
  NeedsImprovement: "text-status-info",
};

const STATUS_BADGE: Record<string, "draft" | "review" | "finalized" | "archived" | "error" | "info" | "gold"> = {
  Pending: "draft",
  InProgress: "review",
  Complete: "info",
  Certified: "finalized",
  Declined: "error",
  Waived: "archived",
  NA: "archived",
};

// Was a third copy of both the arithmetic AND the label wording. `daysLabel`
// already produces these exact strings (plus "—" for a null this cannot
// produce), so routing through it removes the duplicate copy as well as the
// duplicate maths.
// `tz` REQUIRED — the row above has the viewer's zone in hand, and a
// default here would silently pin the countdown to Phoenix again.
function daysFromNow(iso: string): string {
  return daysLabel(calendarDaysUntil(iso));
}

export function SetupChecklistRow({
  row,
  catalogCode,
  catalogTitle,
  catalogTier,
  catalogOwnerRole,
  catalogDueDateFormula,
  onOpen,
}: SetupChecklistRowProps) {
  const badge = STATUS_BADGE[row.status] ?? "draft";
  const tierClass = TIER_CLASS[catalogTier] ?? "border-l-border-light";

  return (
    <button
      type="button"
      onClick={onOpen}
      className={`flex w-full items-center justify-between gap-space-3 border border-border-light rounded-md bg-white-pure px-space-3 py-space-2 text-left hover:bg-white-warm transition-colors border-l-4 ${tierClass}`}
      data-testid={`setup-row-${catalogCode}`}
      data-status={row.status}
    >
      <div className="flex items-center gap-space-3 min-w-0">
        <span className="font-display font-bold text-label uppercase tracking-label text-gold-shadow shrink-0">
          {catalogCode}
        </span>
        <span className="text-small text-onyx truncate">{catalogTitle}</span>
      </div>
      <div className="flex items-center gap-space-3 shrink-0">
        <span
          className={`text-caption font-display uppercase tracking-label ${TIER_TEXT_CLASS[catalogTier] ?? "text-gray-mid"}`}
          data-testid="setup-row-tier"
        >
          {loanSetupTierLabel(catalogTier)}
        </span>
        <span className="text-caption text-gray-mid uppercase tracking-label">
          {catalogOwnerRole}
        </span>
        <span className="text-caption text-gray-mid" data-testid="setup-row-due">
          {isDeferredDueDate(row.dueDate)
            ? deferredDueDateMessage(catalogDueDateFormula)
            : daysFromNow(row.dueDate)}
        </span>
        <Badge status={badge}>{row.status}</Badge>
        <ChevronRight size={16} className="text-gray-mid" aria-hidden />
      </div>
    </button>
  );
}
